import React, { useRef, useState } from 'react';
import { Container, Form, Button, Card, Row, Col } from 'react-bootstrap';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const Templatee = () => {
  const componentRef = useRef();

  // State to hold the resume data
  const [resume, setResume] = useState({
    name: 'Ayush Tiwari',
    title: 'Software Development Engineer',  // Initial title
    phone: '8982893647',
    email: 'ayush.tiwari@example.com',
    location: 'Indore, MP',
    summary:
      'With over 1 year of experience as a Software Engineer, I have a proven track record in developing efficient software solutions, implementing best practices, and leading successful projects.',
    experience: [
      {
        company: 'Lockheed Martin',
        position: 'Industrial Engineer',
        duration: '2019 - 2023',
        location: 'Los Angeles, CA',
        details: [
          'Led a team of engineers in the development and implementation of new manufacturing processes.',
          'Implemented a new automated assembly process, reducing assembly time by 25% and saving $100,000 annually.',
        ],
      },
      {
        company: 'Northrop Grumman',
        position: 'Manufacturing Engineer',
        duration: '2016 - 2019',
        location: 'Los Angeles, CA',
        details: [
          'Designed and implemented a new quality control system, reducing defects by 20%.',
          'Managed a project to upgrade manufacturing equipment, increasing production capacity by 30%.',
        ],
      },
    ],
    education: [
      {
        degree: 'Master of Science in Industrial Engineering',
        university: 'University of Southern California',
        duration: '2011 - 2013',
        location: 'Los Angeles, CA',
      },
      {
        degree: 'Master of Science in Chemistry',
        university: 'University of Michigan',
        duration: '2007 - 2011',
        location: 'Michigan',
      },
    ],
    skills: ['LEAN Manufacturing', 'Root Cause Analysis', 'CAD (Creo, SolidWorks, NX, Catia)'],
    achievements: [
      'Automated Assembly Process: Developed a new automated assembly process that reduced assembly time by 25%.',
      'Factory Layout Redesign: Led a project to redesign the factory layout, improving workflow and increasing productivity by 15%.',
    ],
    projects: [
      {
        title: 'Automated Assembly Process',
        description: 'Developed a new automated assembly process that reduced assembly time by 25%.',
        duration: '2022 - 2023',
      },
    ],
    awards: [
      'Employee of the Year - Lockheed Martin (2022)',
      'Best Project Award - Northrop Grumman (2018)',
    ],
    languages: ['English (Fluent)', 'Hindi (Native)'],
  });

  // Function to generate summary based on title
  const generateSummaryBasedOnTitle = (title) => {
    let summary = '';

    switch (title.toLowerCase()) {
      case 'software development engineer':
        summary =
          'An experienced Software Development Engineer skilled in designing, developing, and maintaining software systems. Proficient in languages such as JavaScript, Python, and C++, with a strong understanding of software architecture and development methodologies.';
        break;
      case 'industrial engineer':
        summary =
          'An Industrial Engineer with expertise in optimizing manufacturing processes, improving efficiency, and implementing cost-saving measures. Experienced in using Lean and Six Sigma methodologies to streamline production and reduce waste.';
        break;
      case 'manufacturing engineer':
        summary =
          'A Manufacturing Engineer with a proven track record in designing and improving manufacturing processes, equipment, and systems. Adept at troubleshooting issues, reducing costs, and increasing production capacity.';
        break;
      default:
        summary =
          'A dedicated professional with expertise in various engineering fields. Passionate about leveraging technical skills to drive innovation and efficiency in processes and systems.';
        break;
    }

    return summary;
  };

  // Handle Title Change and generate Summary automatically
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    const generatedSummary = generateSummaryBasedOnTitle(newTitle);

    setResume((prevState) => ({
      ...prevState,
      title: newTitle,
      summary: generatedSummary, // Automatically update summary based on title
    }));
  };

  const handleChange = (e, field) => {
    setResume({ ...resume, [field]: e.target.value });
  };

  const handleSaveAsPDF = () => {
    const input = componentRef.current;

    // Set the dimensions for A4 size (in pixels at 96dpi)
    const a4Width = 794; // 210mm in pixels
    const a4Height = 1123; // 297mm in pixels

    // Scale the content to fit A4 size
    const scale = Math.min(a4Width / input.offsetWidth, a4Height / input.offsetHeight);

    html2canvas(input, {
      scale: scale,
      width: input.offsetWidth,
      height: input.offsetHeight,
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'px', [a4Width, a4Height]);
      pdf.addImage(imgData, 'PNG', 0, 0, a4Width, a4Height);
      pdf.save('resume.pdf');
    });
  };

  return (
    <Container style={{ marginTop: '30px' }}>
      <Row>
        <Col md={6}>
          <Card>
            <Card.Body>
              <h4>Edit Resume</h4>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={resume.name}
                  onChange={(e) => handleChange(e, 'name')}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={resume.title}
                  onChange={handleTitleChange} // Updated to handle title change
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  value={resume.phone}
                  onChange={(e) => handleChange(e, 'phone')}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="text"
                  value={resume.email}
                  onChange={(e) => handleChange(e, 'email')}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Location</Form.Label>
                <Form.Control
                  type="text"
                  value={resume.location}
                  onChange={(e) => handleChange(e, 'location')}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Summary</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={resume.summary}
                  onChange={(e) => handleChange(e, 'summary')}
                />
              </Form.Group>
              <hr />
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <div ref={componentRef}>
            <Card>
              <Card.Body>
                <h4>{resume.name}</h4>
                <p>{resume.title}</p>
                <p>{resume.phone}</p>
                <p>{resume.email}</p>
                <p>{resume.location}</p>
                <hr />
                <h5>Summary</h5>
                <p>{resume.summary}</p>
                <hr />
                <Button onClick={handleSaveAsPDF} className="mb-3">
                  Save as PDF
                </Button>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Templatee;
